<template>
    <div>
        <div class="container-fluid substitutes-bad-page">
            <div class="card shadow-sm">
                <div class="card-body">
                    <div
                        v-if="isLoading"
                        class="overlay-spinner d-flex justify-content-center align-items-center"
                    >
                        <div class="spinner-border text-danger" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                    </div>
                    <div>
                        <h2 class="mb-3">BAD Substitute Requests</h2>
                    </div>
                    <div v-if="loadError" class="alert alert-danger">
                        {{ loadError }}
                    </div>

                    <ul class="nav nav-tabs mt-3" role="tablist">
                        <li class="nav-item" role="presentation">
                            <button
                                type="button"
                                class="nav-link"
                                :class="{ active: activeTab === 'pending' }"
                                @click="activeTab = 'pending'"
                            >
                                Pending
                                <span class="badge bg-secondary ms-2">
                                    {{ pendingRequests.length }}
                                </span>
                            </button>
                        </li>
                        <li class="nav-item" role="presentation">
                            <button
                                type="button"
                                class="nav-link"
                                :class="{ active: activeTab === 'approved' }"
                                @click="activeTab = 'approved'"
                            >
                                Unlinked
                                <span class="badge bg-secondary ms-2">
                                    {{ approvedRequests.length }}
                                </span>
                            </button>
                        </li>
                        <li class="nav-item" role="presentation">
                            <button
                                type="button"
                                class="nav-link"
                                :class="{ active: activeTab === 'denied' }"
                                @click="activeTab = 'denied'"
                            >
                                Retained
                                <span class="badge bg-secondary ms-2">
                                    {{ deniedRequests.length }}
                                </span>
                            </button>
                        </li>
                    </ul>

                    <div
                        v-if="!isLoading && !visibleRequests.length && !loadError"
                        class="alert alert-info mt-3"
                    >
                        {{ emptyStateMessage }}
                    </div>

                    <div class="table-responsive mt-3" v-if="visibleRequests.length">
                        <table class="table align-middle table-hover">
                            <thead>
                                <tr>
                                    <th scope="col">
                                        Part Number
                                        <button
                                            class="btn btn-outline-secondary btn-refresh"
                                            @click="refreshRequests"
                                            :disabled="isLoading"
                                        >
                                            <span
                                                v-if="isLoading"
                                                class="spinner-border spinner-border-sm text-danger me-2"
                                                role="status"
                                            ></span>
                                            <font-awesome-icon
                                                v-else
                                                :icon="['fas', 'arrows-rotate']"
                                            />
                                        </button>
                                    </th>
                                    <th scope="col">Substitute</th>
                                    <th scope="col">Vendors</th>
                                    <th scope="col">Requested By</th>
                                    <th scope="col">Note</th>
                                    <th scope="col" class="text-end">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="request in visibleRequests" :key="request.id">
                                    <td>
                                        <div class="d-flex align-items-center gap-2">
                                            <div class="fw-semibold mb-0">
                                                {{ request.part_number }}
                                            </div>
                                            <span
                                                v-if="statusIconConfig(request)"
                                                ref="statusTooltip"
                                                class="status-indicator"
                                                :class="statusIconConfig(request).class"
                                                role="img"
                                                :aria-label="statusIconConfig(request).tooltip"
                                                data-bs-toggle="tooltip"
                                                data-bs-placement="top"
                                                :data-bs-title="statusIconConfig(request).tooltip"
                                            >
                                                <font-awesome-icon
                                                    :icon="statusIconConfig(request).icon"
                                                />
                                            </span>
                                        </div>
                                        <small class="text-muted text-uppercase">
                                            {{ request.source || 'N/A' }}
                                        </small>
                                    </td>
                                    <td>
                                        <div class="fw-semibold">
                                            {{ request.substitute_part_number }}
                                        </div>
                                        <small class="text-muted">
                                            {{ request.original_substitute_column_name }}
                                        </small>
                                    </td>
                                    <td>
                                        <ul
                                            v-if="cleanVendors(request.vendors).length"
                                            class="mb-0 ps-3 text-wrap"
                                        >
                                            <li
                                                v-for="vendor in cleanVendors(request.vendors)"
                                                :key="vendor"
                                            >
                                                {{ vendor }}
                                            </li>
                                        </ul>
                                        <div v-else>—</div>
                                    </td>
                                    <td>
                                        <div>{{ getUserName(request) }}</div>
                                        <small class="text-muted"
                                            >on {{ formatDate(request.created_at) }}</small
                                        >
                                    </td>
                                    <td>
                                        <p class="mb-0 text-wrap">{{ request.note || '—' }}</p>
                                    </td>
                                    <td class="text-end">
                                        <div class="btn-group">
                                            <button
                                                type="button"
                                                class="btn btn-sm btn-secondary"
                                                @click="handleDecision(request, true)"
                                                :disabled="
                                                    !isPending(request) || isProcessing(request)
                                                "
                                            >
                                                <span
                                                    v-if="
                                                        isProcessing(request) &&
                                                        pendingDecision(request) === 'approve'
                                                    "
                                                    class="spinner-border spinner-border-sm me-1"
                                                ></span>
                                                Unkink
                                            </button>
                                            <button
                                                type="button"
                                                class="btn btn-sm btn-outline-secondary"
                                                @click="handleDecision(request, false)"
                                                :disabled="
                                                    !isPending(request) || isProcessing(request)
                                                "
                                            >
                                                <span
                                                    v-if="
                                                        isProcessing(request) &&
                                                        pendingDecision(request) === 'deny'
                                                    "
                                                    class="spinner-border spinner-border-sm me-1"
                                                ></span>
                                                Retain
                                            </button>
                                        </div>
                                        <div
                                            v-if="rowError(request)"
                                            class="text-danger small mt-1 text-end"
                                        >
                                            {{ rowError(request) }}
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { toast } from 'vue3-toastify'
    import { Tooltip } from 'bootstrap'
    import { mapStores } from 'pinia'
    import { useSubstitutesStore } from '@/stores/substitutesStore'

    const dateFormatter = new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        //hour: '2-digit',
        //minute: '2-digit',
    })

    const statusDateFormatter = new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
    })

    export default {
        name: 'SubstitutesBadRequestsPage',
        components: {},
        data() {
            return {
                displayQuoteButton: false,
                is_bpa_noquote: false,
                lastDecision: {},
                statusTooltipInstances: [],
                activeTab: 'pending',
            }
        },
        watch: {
            visibleRequests() {
                this.$nextTick(() => {
                    this.initStatusTooltips()
                })
            },
            activeTab() {
                this.$nextTick(() => {
                    this.initStatusTooltips()
                })
            },
        },
        computed: {
            ...mapStores(useSubstitutesStore),
            badSubstituteRequests() {
                return this.substitutesStore.badSubstituteRequests
            },
            badSubstituteRequestsLoading() {
                return this.substitutesStore.badSubstituteRequestsLoading
            },
            badSubstituteRequestsError() {
                return this.substitutesStore.badSubstituteRequestsError
            },
            sortedRequests() {
                const requests = Array.isArray(this.badSubstituteRequests)
                    ? [...this.badSubstituteRequests]
                    : []
                const priority = { pending: 0, approved: 1, denied: 2 }
                return requests.sort((a, b) => {
                    const weightA = priority[a?.status] ?? 99
                    const weightB = priority[b?.status] ?? 99
                    if (weightA === weightB) {
                        return new Date(b.created_at) - new Date(a.created_at)
                    }
                    return weightA - weightB
                })
            },
            isLoading() {
                return this.badSubstituteRequestsLoading
            },
            loadError() {
                return this.badSubstituteRequestsError
            },
            pendingRequests() {
                return this.sortedRequests.filter((request) => this.isPending(request))
            },
            approvedRequests() {
                return this.sortedRequests.filter((request) => this.isApproved(request))
            },
            deniedRequests() {
                return this.sortedRequests.filter((request) => this.isDenied(request))
            },
            visibleRequests() {
                if (this.activeTab === 'approved') return this.approvedRequests
                if (this.activeTab === 'denied') return this.deniedRequests
                return this.pendingRequests
            },
            emptyStateMessage() {
                if (this.activeTab === 'pending') {
                    return 'No pending BAD substitute requests at the moment.'
                }
                if (this.activeTab === 'approved') {
                    return 'No unlinked BAD substitute requests yet.'
                }
                return 'No retained BAD substitute requests yet.'
            },
        },
        mounted() {
            this.refreshRequests().finally(() => {
                this.$nextTick(() => this.initStatusTooltips())
            })
        },
        beforeUnmount() {
            this.destroyStatusTooltips()
        },
        methods: {
            async refreshRequests() {
                try {
                    await this.substitutesStore.fetchBadSubstituteRequests()
                } catch (error) {
                    console.error('Unable to load BAD substitute requests', error)
                }
            },
            getUserName(request) {
                if (!request || !request.user) return 'Unknown'
                const first = request.user.first_name || ''
                const last = request.user.last_name || ''
                const combined = `${first} ${last}`.trim()
                return request.user.full_name || combined || 'Unknown'
            },
            formatDate(value) {
                if (!value) return 'N/A'
                try {
                    const date = new Date(value)
                    return dateFormatter.format(date)
                } catch (error) {
                    return value
                }
            },
            getStatus(request) {
                return (request?.status || '').toLowerCase()
            },
            cleanVendors(vendors) {
                if (!Array.isArray(vendors)) return []
                return vendors.filter(Boolean)
            },
            isPending(request) {
                return this.getStatus(request) === 'pending'
            },
            isApproved(request) {
                return this.getStatus(request) === 'approved'
            },
            isDenied(request) {
                return this.getStatus(request) === 'denied'
            },
            requestStatus(request) {
                if (!request) return { processing: false, error: null }
                const statusGetter = this.substitutesStore.badSubstituteRequestStatusById
                if (typeof statusGetter !== 'function') {
                    return { processing: false, error: null }
                }
                return statusGetter(request.id)
            },
            isProcessing(request) {
                return Boolean(this.requestStatus(request).processing)
            },
            rowError(request) {
                return this.requestStatus(request).error
            },
            pendingDecision(request) {
                if (!request?.id) return null
                return this.lastDecision[request.id]
            },
            statusIconConfig(request) {
                if (!request) return null
                const status = this.getStatus(request)
                const formattedDate = this.formatStatusDate(
                    request.updated_at || request.created_at
                )

                if (status === 'approved') {
                    return {
                        icon: ['fas', 'circle-check'],
                        class: 'text-success',
                        tooltip: formattedDate ? `Approved on ${formattedDate}` : 'Approved',
                    }
                }

                if (status === 'denied') {
                    return {
                        icon: ['fas', 'circle-xmark'],
                        class: 'text-danger',
                        tooltip: formattedDate ? `Denied on ${formattedDate}` : 'Denied',
                    }
                }

                return {
                    icon: ['fas', 'circle-exclamation'],
                    class: 'text-warning',
                    tooltip: 'Pending approval',
                }
            },
            formatStatusDate(value) {
                if (!value) return ''
                try {
                    return statusDateFormatter.format(new Date(value))
                } catch (error) {
                    return ''
                }
            },
            async handleDecision(request, approve) {
                if (!request?.id) return
                this.lastDecision = {
                    ...this.lastDecision,
                    [request.id]: approve ? 'approve' : 'deny',
                }
                try {
                    await this.substitutesStore.reviewBadSubstituteRequest({
                        id: request.id,
                        approve,
                    })
                    if (approve) {
                        this.clearSubstituteCache(request?.part_number)
                    }
                    toast(approve ? 'Substitute is now unlinked' : 'Substitute is retained', {
                        type: approve ? 'success' : 'info',
                        pauseOnFocusLoss: false,
                    })
                } catch (error) {
                    toast('Failed to update substitute request.', {
                        type: 'error',
                        pauseOnFocusLoss: false,
                    })
                } finally {
                    this.lastDecision = {
                        ...this.lastDecision,
                        [request.id]: null,
                    }
                }
            },
            clearSubstituteCache(partNumber) {
                if (typeof window === 'undefined' || !window.localStorage) {
                    return
                }
                const normalized = (partNumber || '').toString().trim().toUpperCase()
                if (!normalized) {
                    return
                }
                try {
                    const cacheKey = `substitutes_${encodeURIComponent(normalized)}`
                    window.localStorage.removeItem(cacheKey)
                } catch (error) {
                    console.warn('Failed to clear substitutes cache', error)
                }
            },
            initStatusTooltips() {
                this.destroyStatusTooltips()
                const tooltipRefs = this.$refs.statusTooltip
                if (!tooltipRefs) return
                const elements = Array.isArray(tooltipRefs) ? tooltipRefs : [tooltipRefs]
                this.statusTooltipInstances = elements
                    .filter((el) => el)
                    .map((el) => new Tooltip(el))
            },
            destroyStatusTooltips() {
                if (!Array.isArray(this.statusTooltipInstances)) {
                    this.statusTooltipInstances = []
                    return
                }
                this.statusTooltipInstances.forEach((instance) => {
                    if (instance && typeof instance.dispose === 'function') {
                        instance.dispose()
                    }
                })
                this.statusTooltipInstances = []
            },
        },
    }
</script>

<style scoped lang="scss">
    .substitutes-bad-page {
        padding: 2rem;
    }

    .substitutes-bad-page > .card {
        border: 1px solid var(--color-border-default);
        border-top: 4px solid var(--color-brand-primary);
        border-radius: var(--radius-md);
        background: var(--color-surface-canvas);
        box-shadow: var(--shadow-sm);
    }

    .card-body {
        position: relative;
        background: var(--color-surface-canvas);
        color: var(--color-text-primary);
    }

    h2 {
        font-size: 22px;
        text-align: center;
        color: var(--color-brand-primary);
    }

    .nav-tabs {
        border-bottom-color: var(--color-border-default);
    }

    .nav-tabs .nav-link {
        color: var(--color-text-secondary);
        background: transparent;
        border-color: transparent;
    }

    .nav-tabs .nav-link.active {
        color: var(--color-text-primary);
        background: var(--color-surface-canvas);
        border-color: var(--color-border-default) var(--color-border-default)
            var(--color-surface-canvas);
    }

    .table-responsive {
        background: var(--color-surface-canvas);
        border-radius: var(--radius-sm);
    }

    .table {
        background: var(--color-surface-canvas);
        color: var(--color-text-primary);

        thead {
            td,
            th {
                color: var(--color-text-inverse);
                background-color: var(--color-surface-inverse) !important;
            }
        }
        th {
            text-transform: uppercase;
            font-size: 0.75rem;
            letter-spacing: 0.05em;
            color: var(--color-text-inverse);
        }

        td {
            vertical-align: middle;
            max-width: 200px;
            background: var(--color-surface-canvas);
            color: var(--color-text-primary);
            border-color: var(--color-border-muted);
        }
    }

    .btn-refresh {
        color: var(--color-text-inverse);
    }

    .status-indicator {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 1rem;
    }

    @media (max-width: 768px) {
        .substitutes-bad-page {
            padding: 1rem;
        }

        .table {
            font-size: 0.9rem;
        }
    }

    .overlay-spinner {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: var(--color-surface-overlay-soft);
        z-index: 10;
    }
</style>
